<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm
    }"
  >
    <div class="q-mb-md row">
      <div
        class="q-my-none col text-overline text-dark text-uppercase"
      >
        {{ $t('run.prepareGroupSession.textTitle') }}
      </div>
    </div>
    <div class="row q-col-gutter-md">
      <div
        v-for="groupParticipant in sessionGroup"
        :key="groupParticipant.id"
        class="col-12"
      >
        <q-card>
          <q-card-section horizontal>
            <q-card-section class="col-auto">
              <q-avatar size="64px">
                <img :src="getClientPhoto(groupParticipant)">
              </q-avatar>
            </q-card-section>
            <q-card-section class="col row">
              <template v-if="hasLinkedAppointment(groupParticipant.appointment)">
                <appointment-chip
                  v-if="isOwnAppointment(groupParticipant.appointment)"
                  :appointment="getLinkedAppointment(groupParticipant.appointment)"
                  size="sm"
                  :is-group-session="isGroupSession(groupParticipant)"
                  class="cursor-pointer q-ml-none"
                  @click="onEditAppointment(groupParticipant)"
                />
                <universal-tooltip
                  v-else
                  show-tooltip
                  :text="$t('run.prepareGroupSession.notOwnAppointment.message', {
                    dateTime: getAppointmentDateTime(groupParticipant.appointment)
                  })"
                  style="max-width: 100%;"
                >
                  <q-chip
                    color="negative"
                    icon="event"
                    size="sm"
                    clickable
                    square
                    outline
                    @click.stop="onEditAppointment(groupParticipant)"
                  >
                    <div class="ellipsis">
                      {{ $t('run.prepareGroupSession.notOwnAppointment.title') }}
                    </div>
                  </q-chip>
                </universal-tooltip>
              </template>
              <div class="text-subtitle2 ellipsis col-12">
                {{ groupParticipant.sessionName }}
              </div>
              <div class="ellipsis col-12">
                {{ groupParticipant.clientName }}
              </div>
              <div class="text-grey-6 ellipsis col-12">
                {{ groupParticipant.userName }}
              </div>
            </q-card-section>
            <q-card-section
              class="col-auto items-top"
            >
              <div class="column q-col-gutter-sm items-end">
                <div class="row q-col-gutter-sm items-center">
                  <universal-tooltip
                    v-if="canLinkToAppointment(groupParticipant)"
                    show-tooltip
                    :text="$t(isOwnActivity(groupParticipant)
                      ? 'run.prepareGroupSession.tooltipLinkAppointment'
                      : 'run.prepareGroupSession.tooltipLinkAppointmentUnavailable'
                    )"
                  >
                    <q-btn
                      v-ripple
                      :disable="!isOwnActivity(groupParticipant)"
                      icon="calendar_month"
                      color="dark"
                      flat
                      round
                      @click="onEditAppointment(groupParticipant)"
                    />
                  </universal-tooltip>
                  <q-btn
                    v-ripple
                    icon="group_remove"
                    color="dark"
                    :disable="groupParticipant.locked"
                    flat
                    round
                    @click="onRemoveParticipantFromGroup(groupParticipant)"
                  >
                    <q-tooltip>
                      {{ $t('run.prepareGroupSession.tooltipRemoveFromGroup') }}
                    </q-tooltip>
                  </q-btn>
                </div>
                <div
                  class="row items-center q-mt-xs justify-end cursor-pointer text-grey-10 "
                  @click.stop="togglePreparation(groupParticipant.id)"
                >
                  <span class="text-uppercase text-caption text-weight-medium q-mr-sm">
                    {{ $t('run.overview.textTitle') }}
                  </span>
                  <q-icon
                    :name="expanded[groupParticipant.id] ? 'expand_less' : 'expand_more'"
                    class="prep-icon"
                  />
                </div>
              </div>
            </q-card-section>
          </q-card-section>
          <q-card-section class="q-pa-none">
            <q-expansion-item
              v-model="expanded[groupParticipant.id]"
              header-style="display: none;"
              class="no-header-chev"
              hide-expand-icon
            >
              <template #default>
                <q-card-section class="q-pt-md">
                  <banner
                    v-if="showAppointmentNoteBanner(groupParticipant.appointment)"
                    :heading="$t('run.overview.appointmentNote')"
                    :message="getLinkedAppointment(groupParticipant.appointment).notes"
                    variant="multi-line"
                    subtype="note"
                    class="appointment-note-banner q-mb-md"
                  />
                  <session-preparation-content
                    :session-id="groupParticipant.originalSession"
                    :client-id="groupParticipant.client"
                    :client="getParticipantClient(groupParticipant)"
                  />
                </q-card-section>
              </template>
            </q-expansion-item>
          </q-card-section>
        </q-card>
      </div>
      <div
        class="col-12"
      >
        <q-card>
          <q-btn
            v-ripple
            color="primary"
            :label="$t('run.prepareGroupSession.buttonAddGroupParticipant')"
            icon="group_add"
            flat
            class="full-width"
            data-e2e="button-add-group-participant"
            @click="onAddGroupParticipant"
          />
        </q-card>
      </div>
    </div>
    <div
      v-if="offlineMode"
      class="q-my-md row"
    >
      <div
        class="q-my-none col text-overline text-red text-uppercase"
      >
        {{ $t('run.prepareGroupSession.notAllowedInOfflineMode') }}
      </div>
    </div>
    <div class="q-py-md q-gutter-sm">
      <q-btn
        v-ripple
        color="dark-variant"
        :label="$t('common.buttonCancel')"
        @click="onCancelSession"
      />
      <q-btn
        v-ripple
        color="positive"
        icon="play_arrow"
        :label="$t('run.prepareGroupSession.buttonStartGroupSession')"
        :loading="isPreparing"
        :disable="offlineMode"
        data-e2e="button-start-group-session"
        @click="onStartSession"
      />
    </div>
    <q-page-sticky
      position="bottom-right"
      :offset="[18, 18]"
    >
      <q-btn
        v-ripple
        fab
        color="positive"
        icon="play_arrow"
        :loading="isPreparing"
        @click="onStartSession"
      />
    </q-page-sticky>
  </q-page>
</template>

<script>
import ProgramInfo from 'components/Programs/ProgramInfo';
import ChangeSessionActivityAppointment from 'components/SessionActivities/Modals/ChangeSessionActivityAppointment';
import AddSessionDialog from 'components/Sessions/Modals/AddSessionDialog';
import SessionPreparationContent from 'components/Sessions/SessionPreparationContent';
import AppointmentChip from 'components/UI/AppointmentChip';
import Banner from 'components/UI/Banner.vue';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { Timestamp } from 'firebase/firestore';
import { chain, find, get, map, reject } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { useClients } from 'src/composables/clients';
import { useTime } from 'src/composables/time';
import AppointmentsDB from 'src/fb/appointments-db';
import GenericDb from 'src/fb/generic-db';
import SessionGroupsDB from 'src/fb/sessionGroups-db';
import SessionsDB from 'src/fb/sessions-db';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import Personalize from 'src/mixins/Personalize';
import SessionsMixin from 'src/mixins/SessionsMixin';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { geolocation } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { sessions } from 'src/store/sessions';
import { userInterface } from 'src/store/userInterface';
import AppUtils from 'src/utils/AppUtils';
import { clearClientBackupData, clearClientBackupNotes } from 'src/utils/backupUtils';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'PrepareGroupSession',

  components: {
    Banner,
    UniversalTooltip,
    AppointmentChip,
    SessionPreparationContent,
  },

  mixins: [
    Integrations,
    Interactions,
    Personalize,
    SessionsMixin,
  ],

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      /**
       * To make the UI as fast as possible we load and show programs from the scheduled / ad-hoc
       * session immediately on load. At the same time we ask a back-end function to prepare the
       * new session for us. We then load the new active session in the background.
       */
      vm.$q.loading.show();
      vm.finishActiveSession();
      const { sessionId } = vm.$route.params;
      const appointmentId = get(vm.$route, ['query', 'appointment'], null);
      vm.closeLeftMenuDrawer();

      await vm.prepareSessionGroup(sessionId, appointmentId);
      const sessionParticipationId = chain(vm.sessionGroup)
        .head()
        .get('id')
        .value();
      vm.prepareGroupParticipantSession(sessionParticipationId, true);
      if (appointmentId) {
        await vm.prepareAppointmentGroup(appointmentId);
      }
      vm.$q.loading.hide();
    });
  },

  setup() {
    const { getClientProfileImagePath } = useClients();
    const { getLocaleDateString } = useTime();

    return { getClientProfileImagePath, getLocaleDateString };
  },

  data() {
    return {
      preparingCount: 0,

      sessionGroup: [],

      session: null,
      linkedAppointments: {},

      sessionId: null,
      appointmentId: null,
      groupSessionRefId: null,
      expanded: {},
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'currentClient', 'clientAppointmentsUpcoming',
      'hasTrainerCredentials']),
    ...mapState(sessions, ['scheduledSessions', 'activeSession']),
    ...mapState(network, ['offlineMode']),
    ...mapState(clients, ['clients']),

    isPreparing() {
      return this.preparingCount > 0;
    },
  },

  methods: {
    ...mapActions(sessions, [
      'loadActiveSession',
      'cancelActiveSession',
      'finishActiveSession',
      'switchActiveSession',
    ]),
    ...mapActions(userInterface, ['openLeftMenuDrawer', 'closeLeftMenuDrawer']),
    ...mapActions(geolocation, ['refreshGeolocation']),

    async loadLinkedAppointment(appointmentId) {
      if (!appointmentId) {
        return null;
      }

      let linkedAppointment = chain(this.clientAppointmentsUpcoming)
        .find({ id: appointmentId })
        .defaultTo(null)
        .value();

      if (linkedAppointment !== null) {
        return linkedAppointment;
      }
      const appointmentsDb = new AppointmentsDB();
      linkedAppointment = await appointmentsDb.read(appointmentId) || null;
      if (linkedAppointment !== null) {
        return linkedAppointment;
      }
      return null;
    }, // loadLinkedAppointment()

    async prepareSessionGroup(sessionId, appointmentId = null) {
      const session = await this.loadSession(sessionId);

      if (get(session, 'isDeleted') === true) {
        this.notifyNegative(this.$t('run.overview.notifySessionDeleted'));
        this.$router.push('/');
        throw new Error('Session is deleted');
      }

      let sessionGroupAppointment = null;
      if (appointmentId) {
        const appointment = await this.loadLinkedAppointment(appointmentId);
        if (appointment) {
          this.linkedAppointments[appointmentId] = appointment;
          this.groupSessionRefId = appointment.groupSessionRefId || null;
          sessionGroupAppointment = appointmentId;
        }
      }
      this.sessionGroup = [
        {
          id: uid(),
          originalSession: sessionId || null,
          session: null,
          sessionName: session?.sessionName || null,
          client: this.currentClient.id,
          clientName: this.currentClient.clientName,
          user: this.userDetails.userId,
          userName: this.userDetails.name,
          appointment: sessionGroupAppointment,
          locked: true,
        },
      ];
    }, // prepareSessionGroup()

    async prepareAppointmentGroup(appointmentId) {
      if (!this.groupSessionRefId || !this.isOwnAppointment(appointmentId)) {
        return;
      }

      const appointmentsDb = new AppointmentsDB();
      const groupAppointments = await appointmentsDb.readAll([
        ['clinic', '==', this.userDetails.clinic],
        ['status', '==', 'scheduled'],
        ['groupSessionRefId', '==', this.groupSessionRefId],
      ]);

      const groupParticipantActions = chain(groupAppointments)
        .reject({ id: appointmentId })
        .map(async (groupAppointment) => {
          if (!groupAppointment.session) {
            return;
          }
          const session = await this.loadSession(groupAppointment.session);
          if (!session || session.isDeleted) {
            return;
          }
          const client = find(this.clients, { id: groupAppointment.client });
          if (!client) {
            return;
          }

          return {
            id: uid(),
            originalSession: groupAppointment.session,
            session: null,
            sessionName: session.sessionName,
            client: groupAppointment.client,
            clientName: client.clientName,
            user: groupAppointment.user,
            userName: groupAppointment.userName,
            appointment: groupAppointment.id,
            locked: false,
          };
        })
        .value();
      const groupParticipants = chain(await AppUtils.allSettled(groupParticipantActions))
        .filter({ status: 'fulfilled' })
        .map('value')
        .compact()
        .value();

      this.linkedAppointments = {
        ...this.linkedAppointments,
        ...chain(groupAppointments)
          .map((groupAppointment) => [groupAppointment.id, groupAppointment])
          .fromPairs()
          .value(),
      };

      this.sessionGroup = this.sessionGroup.concat(groupParticipants);

      const prepareActions = map(
        groupParticipants,
        (groupParticipant) => (this.prepareGroupParticipantSession(groupParticipant.id, false)),
      );
      Promise.all(prepareActions);
    }, // prepareAppointmentGroup()

    async onAddGroupParticipant() {
      this.openComponentDialog(
        AddSessionDialog,
        {
          title: this.$t('sessions.modals.addGroupParticipant.textTitle'),
        },
        async (groupParticipant) => {
          if (!groupParticipant) {
            return;
          }
          const sessionParticipationId = uid();
          this.sessionGroup.push({
            ...groupParticipant,
            session: null,
            id: sessionParticipationId,
          });

          try {
            await this.prepareGroupParticipantSession(sessionParticipationId, false);
          } catch (err) {
            this.sessionGroup = reject(this.sessionGroup, { id: sessionParticipationId });
          }
        },
      );
    }, // onAddGroupParticipant()

    onRemoveParticipantFromGroup(groupParticipant) {
      if (groupParticipant.locked) {
        return;
      }
      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t(
          'run.prepareGroupSession.confirmRemoveParticipantFromGroup',
          { participantName: groupParticipant.clientName },
        ),
        () => {
          this.sessionGroup = reject(this.sessionGroup, { id: groupParticipant.id });
        },
      );
    },

    onShowProgramInfo(program) {
      this.openComponentDialog(ProgramInfo, { program });
    }, // onShowProgramInfo()

    onEditAppointment(groupParticipant) {
      const disableAppointments = chain(this.sessionGroup)
        .map('appointment')
        .compact()
        .reject((appointmentId) => (appointmentId === groupParticipant.appointment))
        .value();

      this.openComponentDialog(ChangeSessionActivityAppointment, {
        appointmentId: groupParticipant.appointment || null,
        clientId: groupParticipant.client,
        userId: groupParticipant.user,
        startDate: new Date(),
        allowUnlink: this.isOwnAppointment(groupParticipant.appointment),
        disableAppointments,
      }, async (newAppointmentId) => {
        if (newAppointmentId === null) {
          this.unlinkAppointment(groupParticipant);
          return;
        }
        this.$q.loading.show();
        try {
          await this.linkAppointment(groupParticipant, newAppointmentId);
        } finally {
          this.$q.loading.hide();
        }
      });
    }, // onEditAppointment()

    /**
     * Start a new session by updating session info in Firestore
     */
    async onStartSession() {
      this.$q.loading.show();

      const sessionGroupInfo = {
        clinic: this.userDetails.clinic,
        createdBy: this.userDetails.userId,
        groupSessionRefId: this.groupSessionRefId,
        groupParticipants: map((this.sessionGroup), (groupParticipant, idx) => {
          return {
            client: groupParticipant.client,
            session: groupParticipant.session,
            order: idx + 1,
          };
        }),
      };

      const sessionsDb = new SessionsDB();
      const sessionGroupsDb = new SessionGroupsDB();
      const genericDb = new GenericDb();

      try {
        // should use backend function in case if offline mode is not used
        if (!this.offlineMode) {
          const newSessionGroup = await sessionGroupsDb.create(sessionGroupInfo);
          const sessionGroupId = get(newSessionGroup, 'id', null);
          await Promise.all(
            this.sessionGroup.map(async (groupParticipant) => {
              const appointmentId = this.isOwnAppointment(groupParticipant.appointment)
                ? groupParticipant.appointment
                : null;
              const startSessionOptions = {
                sessionId: groupParticipant.session, appointmentId, sessionGroupId, userId: groupParticipant.user,
              };
              await this.startRunningSessionViaBackend(startSessionOptions);
            }),
          );
        } else {
          await genericDb.executeTransaction(async (transactionMethods) => {
            const newSessionGroup = await transactionMethods.create(sessionGroupsDb, { data: sessionGroupInfo });
            const sessionGroupId = get(newSessionGroup, 'id', null);

            const sessionStartActions = map(this.sessionGroup, async (groupParticipant) => {
              const session = await sessionsDb.read(groupParticipant.session);
              if (!session) {
                return;
              }

              const appointmentId = this.isOwnAppointment(groupParticipant.appointment)
                ? groupParticipant.appointment
                : null;

              return this.startRunningSession(
                session,
                groupParticipant.user,
                groupParticipant.userName,
                null,
                appointmentId,
                sessionGroupId,
                transactionMethods,
              );
            });
            await Promise.all(sessionStartActions);
          });
        }
      } catch (err) {
        this.notifyNegative(this.$t('run.collectData.sessionNotActiveText'));
        logEvent(err);

        this.$q.loading.hide();
        this.$router.push('/');
        return;
      }

      this.refreshGeolocation(true);

      this.$q.loading.hide();

      const dataCollectionPage = {
        name: 'session-data-collection',
        params: {
          sessionId: this.activeSession.id,
        },
        query: {
          shouldForceUpdate: true,
        },
      };

      await clearClientBackupData(this.currentClient.id);
      await clearClientBackupNotes(this.currentClient.id);
      this.$router.push(dataCollectionPage);
    }, // onStartSession()

    onCancelSession() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('run.overview.dialogCancelSessionMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          this.$q.loading.show();
          try {
            this.cancelActiveSession();
            this.openLeftMenuDrawer();
          } finally {
            this.$q.loading.hide();
          }
          this.$router.push('/');
        });
    }, // onCancelSession()

    async switchToSession(sessionId) {
      this.finishActiveSession();
      await this.loadActiveSession({
        sessionId,
        forceUpdate: true,
      });
      return this.switchActiveSession({ sessionId });
    }, // switchToSession()

    async loadSession(sessionId) {
      const session = chain(this.scheduledSessions).find({ id: sessionId }).defaultTo(null).value();
      if (session !== null) {
        return session;
      }
      const sessionsDb = new SessionsDB(sessionId);
      return sessionsDb.read(sessionId);
    }, // loadSession()

    async prepareGroupParticipantSession(sessionParticipationId, isPrimarySession = false) {
      const sessionParticipation = find(this.sessionGroup, { id: sessionParticipationId });
      const originalSessionId = sessionParticipation?.originalSession || null;
      if (originalSessionId === null) {
        throw new Error('Session initialization error');
      }

      this.preparingCount += 1;

      sessionParticipation.session = await this.prepareSession(originalSessionId);
      if (!sessionParticipation.session) {
        this.notifyNegative(this.$t('run.prepareGroupSession.notifySessionNotPrepared', {
          participantName: sessionParticipation.userName ?? '',
        }));
        throw new Error('Session is not prepared');
      }

      if (isPrimarySession) {
        await this.switchToSession(sessionParticipation.session);
      }

      this.preparingCount -= 1;
    }, // prepareGroupParticipantSession()

    async linkAppointment(groupParticipant, appointmentId) {
      const appointment = await this.loadLinkedAppointment(appointmentId);
      if (appointment) {
        this.linkedAppointments[appointmentId] = appointment;
      }

      this.sessionGroup = map(
        this.sessionGroup,
        (participant) => {
          if (participant.id === groupParticipant.id) {
            return {
              ...participant,
              appointment: appointment?.id || null,
            };
          }
          return participant;
        },
      );
    }, // linkAppointment()

    unlinkAppointment(groupParticipant) {
      this.sessionGroup = map(
        this.sessionGroup,
        (participant) => {
          if (participant.id === groupParticipant.id) {
            return {
              ...participant,
              appointment: null,
            };
          }
          return participant;
        },
      );
    }, // unlinkAppointment()

    hasLinkedAppointment(appointmentId) {
      return !!this.linkedAppointments[appointmentId];
    },

    getLinkedAppointment(appointmentId) {
      return this.linkedAppointments[appointmentId];
    },

    isOwnAppointment(appointmentId) {
      return this.getLinkedAppointment(appointmentId)?.user === this.userDetails.userId;
    },

    isOwnActivity(groupParticipant) {
      return groupParticipant.user === this.userDetails.userId;
    },

    getAppointmentDateTime(appointmentId) {
      const scheduledStartTimestamp = this.getLinkedAppointment(appointmentId)?.scheduledStartTimestamp;

      return scheduledStartTimestamp instanceof Timestamp
        ? this.getLocaleDateString(scheduledStartTimestamp, { options: { hour: 'numeric', minute: '2-digit' } }) : '?';
    },

    canLinkToAppointment(groupParticipant) {
      return (groupParticipant.appointment || null) === null;
    },

    isGroupSession(groupParticipant) {
      return !!chain(this.linkedAppointments)
        .get(groupParticipant.appointment)
        .get('groupSessionRefId')
        .value();
    },

    getParticipantClient(groupParticipant) {
      return find(this.clients, { id: groupParticipant.client }) || null;
    }, // getParticipantClient()

    getClientPhoto(groupParticipant) {
      const client = find(this.clients, { id: groupParticipant.client });
      if (!client) {
        return;
      }
      return this.getClientProfileImagePath(client);
    }, // getClientPhoto()

    togglePreparation(id) {
      this.expanded[id] = !this.expanded[id];
    },

    showAppointmentNoteBanner(appointmentId) {
      if (!this.hasLinkedAppointment(appointmentId)) {
        return false;
      }

      const appointment = this.getLinkedAppointment(appointmentId);

      return appointment.notes && (appointment.isNotesVisible ?? true) && this.hasTrainerCredentials;
    }, // showAppointmentNoteBanner()

  },
};
</script>
<style scoped lang="scss">
.prep-icon {
  font-size: 20px;
  line-height: 1;
}
.appointment-note-banner {
  :deep(.banner__heading) {
    font-weight: 600;
    margin-bottom: 8px;
  }
}
</style>
